export default class PreviewManager {
  constructor(controller) {
    this.controller = controller
  }

  createPreviewElement(file) {
    if (!this.controller.hasPreviewContainerTarget) return null

    try {
      const previewItem = this.buildPreviewStructure(file)
      this.loadImagePreview(file, previewItem)
      this.controller.previewContainerTarget.appendChild(previewItem)
      return previewItem
    } catch (error) {
      console.error("Error creating preview element:", error)
      return null
    }
  }

  buildPreviewStructure(file) {
    const previewItem = document.createElement("div")
    previewItem.className = "preview-item"
    previewItem.dataset.filename = file.name

    const loadingOverlay = this.createLoadingOverlay()
    previewItem.appendChild(loadingOverlay)

    const img = document.createElement("img")
    img.className = "image-preview"
    img.file = file
    previewItem.appendChild(img)

    const fileInfo = this.createFileInfo(file)
    previewItem.appendChild(fileInfo.name)
    previewItem.appendChild(fileInfo.size)
    previewItem.appendChild(fileInfo.status)

    return previewItem
  }

  createLoadingOverlay() {
    const overlay = document.createElement("div")
    overlay.className = "preview-loading-overlay"

    const spinner = document.createElement("div")
    spinner.className = "preview-spinner"
    overlay.appendChild(spinner)

    return overlay
  }

  createFileInfo(file) {
    const name = document.createElement("div")
    name.className = "preview-file-name"
    name.textContent = file.name

    const size = document.createElement("div")
    size.className = "file-size"
    size.textContent = this.controller.formatFileSize(file.size)

    const status = document.createElement("div")
    status.className = "preview-file-status"
    status.textContent = "Uploading..."

    return { name, size, status }
  }

  loadImagePreview(file, previewItem) {
    try {
      const reader = new FileReader()
      const img = previewItem.querySelector('img')

      reader.onload = (e) => {
        try {
          img.src = e.target.result
        } catch (err) {
          console.error("Error setting image src:", err)
        }
      }

      reader.onerror = () => {
        console.error("FileReader error")
      }

      reader.readAsDataURL(file)
    } catch (error) {
      console.error("Error with FileReader:", error)
    }
  }

  updatePreviewAfterUpload(previewElement, success, errorMessage = null) {
    if (!previewElement) return

    this.removeLoadingOverlay(previewElement)

    if (success) {
      this.showUploadSuccess(previewElement)
    } else {
      this.showUploadError(previewElement, errorMessage)
    }
  }

  removeLoadingOverlay(previewElement) {
    const loadingOverlay = previewElement.querySelector('div.preview-loading-overlay')
    loadingOverlay?.remove()
  }

  showUploadSuccess(previewElement) {
    const statusMessage = previewElement.querySelector('.preview-file-status')
    if (statusMessage) {
      statusMessage.textContent = "Uploaded successfully"
      statusMessage.className = "preview-status-success"
    }
  }

  showUploadError(previewElement, errorMessage) {
    const statusMessage = previewElement.querySelector('.preview-file-status')
    if (!statusMessage) return

    statusMessage.textContent = errorMessage || "Upload failed"
    statusMessage.className = "preview-status-error"

    this.addRetryButton(previewElement)
    this.highlightErrorState(previewElement)
  }

  addRetryButton(previewElement) {
    const retryButton = document.createElement('button')
    retryButton.type = 'button'
    retryButton.className = 'preview-retry-button'
    retryButton.textContent = 'Retry'

    retryButton.addEventListener('click', () => {
      const file = previewElement.querySelector('img').file
      if (file) {
        previewElement.remove()
        const newPreviewElement = this.createPreviewElement(file)
        if (newPreviewElement) {
          this.controller.fileUploader.uploadFile(file, newPreviewElement)
        }
      }
    })

    previewElement.appendChild(retryButton)
  }

  highlightErrorState(previewElement) {
    const img = previewElement.querySelector('img')
    if (img) {
      img.style.borderColor = "#ef4444"
    }
  }

  clearPreviews() {
    if (this.controller.hasPreviewContainerTarget) {
      this.controller.previewContainerTarget.innerHTML = ""
    }
  }

  recreateAllPreviews() {
    // Clear all image rows that are temporary (not from server)
    this.controller.imageRowTargets.forEach(row => {
      if (row.querySelector('[data-images-field-target="removeButton"]')) {
        row.remove()
      }
    })

    // Show preview container
    this.showPreviewContainer()

    // Process all stored files
    this.controller.storedFiles.forEach(file => {
      const previewElement = this.createPreviewElement(file)
      if (previewElement) {
        this.controller.fileManager.handleFileForNewRecord(file, previewElement)
      }
    })
  }

  hidePreviewContainer() {
    if (this.controller.hasNewFilesPreviewTarget) {
      this.controller.newFilesPreviewTarget.classList.add("hidden")
    }
  }

  showPreviewContainer() {
    if (this.controller.hasNewFilesPreviewTarget) {
      this.controller.newFilesPreviewTarget.classList.remove("hidden")
    }
  }

  removePreviewElement(element) {
    element?.remove()
  }

  checkAndHidePreviewContainer() {
    if (this.controller.hasPreviewContainerTarget && this.controller.previewContainerTarget.children.length === 0) {
      this.hidePreviewContainer()
      this.controller.fileManager.clearSelectedFilesMessage()
    }
  }
}